import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Search, Scissors, BookOpen, DollarSign, ArrowRight, ShieldCheck, ChevronDown } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
export function BenefitsSection() {
  const TIMER_DURATION = 15 * 60; // 15 minutes in seconds
  const STORAGE_KEY = 'crochet_timer_end';
  const [timeLeft, setTimeLeft] = useState<number>(TIMER_DURATION);
  const [guaranteeOpen, setGuaranteeOpen] = useState(false);
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    let endTime: number;
    if (stored) {
      endTime = parseInt(stored, 10);
      const remaining = Math.floor((endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        endTime = Date.now() + TIMER_DURATION * 1000;
        localStorage.setItem(STORAGE_KEY, endTime.toString());
        setTimeLeft(TIMER_DURATION);
      } else {
        setTimeLeft(remaining);
      }
    } else {
      endTime = Date.now() + TIMER_DURATION * 1000;
      localStorage.setItem(STORAGE_KEY, endTime.toString());
      setTimeLeft(TIMER_DURATION);
    }
    const interval = setInterval(() => {
      const remaining = Math.floor((endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        endTime = Date.now() + TIMER_DURATION * 1000;
        localStorage.setItem(STORAGE_KEY, endTime.toString());
        setTimeLeft(TIMER_DURATION);
      } else {
        setTimeLeft(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const minutes = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const seconds = (timeLeft % 60).toString().padStart(2, '0');
  const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const today = dayNames[new Date().getDay()];
  const benefits = [{
    icon: Search,
    title: 'Índice Maestro Inteligente',
    description: 'Encontrá el patrón ideal en segundos. Filtrá por nivel, tipo de prenda y dificultad sin perder tiempo.'
  }, {
    icon: Scissors,
    title: 'Patrones Modernos y Reales',
    description: 'Accedé a diseños actuales con talles reales, adaptados a las modas de hoy. Nada de patrones de 1990.'
  }, {
    icon: BookOpen,
    title: 'Instrucciones Paso a Paso',
    description: 'Tejé con seguridad gracias a guías claras, materiales equivalentes locales y soporte visual.'
  }, {
    icon: DollarSign,
    title: 'Ahorro Real en Uruguay',
    description: 'Guía de proveedores uruguayos y calculadora de costos en pesos para que sepas cuánto cobrar o gastar.'
  }];
  return <section id="oferta" className="py-12 md:py-24 bg-white relative overflow-hidden" data-id="element-1">
      {/* Decorative yarn ball */}
      <div className="absolute -right-20 top-40 w-64 h-64 rounded-full border-[20px] border-[#FAF0E6] opacity-50" data-id="element-2" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-3">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-[0px]" data-id="element-4">
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-5">
            <span className="text-[#C17B5C] font-bold tracking-wider uppercase text-sm mb-1 block" data-id="element-6">
              La Solución
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#3D2B1F] mb-4" data-id="element-7">
              Todo lo que necesitás para tejer sin límites
            </h2>
            <p className="text-base md:text-lg text-[#6B5A4E] mb-6 leading-relaxed" data-id="element-8"></p>

            <ul className="space-y-4 md:space-y-6" data-id="element-9">
              {benefits.map((benefit, index) => <motion.li key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="flex items-start" data-id="element-10">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#E8F3EE] flex items-center justify-center text-[#5C8D75] mt-1" data-id="element-11">
                    <benefit.icon className="w-5 h-5" data-id="element-12" />
                  </div>
                  <div className="ml-3" data-id="element-13">
                    <h3 className="text-base md:text-xl font-bold text-[#3D2B1F]" data-id="element-14">
                      {benefit.title}
                    </h3>
                    <p className="text-sm md:text-base text-[#6B5A4E] mt-0.5" data-id="element-15">
                      {benefit.description}
                    </p>
                  </div>
                </motion.li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="relative" data-id="element-16">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            duration: 0.6
          }} className="mt-6 flex flex-col items-center gap-3 bg-[#FAF7F4] rounded-2xl p-5 border border-[#EAE0D5] shadow-md" id="producto" data-id="element-17">
              <div className="relative w-full mt-2" data-id="element-18">
                {/* Badge sobresaliendo arriba a la izquierda */}
                <div className="absolute -top-3 left-0 z-10 -translate-x-2" data-id="element-19">
                  <span className="inline-flex items-center bg-black text-white text-xs font-bold px-2.5 py-1 rounded-md whitespace-nowrap shadow-lg tracking-tight" data-id="element-20">
                    🎁 Oferta -50% OFF · solo 20 cupos
                  </span>
                </div>
                <img src="https://cdn.magicpatterns.com/uploads/uUwKhSAmRDd1JgfGAdiHQz/LibroDigital_(2).jpg" alt="Biblia Crochet 2026 - Libro Digital" className="w-full max-h-72 rounded-xl object-contain shadow-sm" loading="lazy" decoding="async" data-id="element-21" />
              </div>

              <div className="text-center py-1" data-id="element-22">
                <span className="text-xs text-[#8B7355] uppercase tracking-wider font-bold" data-id="element-23">
                  Precio Oferta
                </span>
                <div className="flex items-center justify-center gap-2 mt-0.5" data-id="element-24">
                  <span className="text-xl text-[#B8C9C0] line-through font-bold" data-id="element-25">
                    $1197
                  </span>
                  <span className="font-bold text-[#C17B5C] text-[28px] leading-none" data-id="element-26">
                    $597
                  </span>
                  <span className="text-base text-[#3D2B1F] font-bold" data-id="element-27">
                    UYU
                  </span>
                </div>
              </div>

              {/* Urgency countdown block */}
              <div className="w-full bg-[#FFF4EE] border border-[#F0C9B0] rounded-xl px-4 py-3 text-center" data-id="element-28">
                <p className="text-[#C17B5C] font-extrabold text-sm uppercase tracking-wider mb-1" data-id="element-29">
                  ¡Oferta hoy {today}!
                </p>
                <p className="text-[#6B5A4E] text-xs leading-snug mb-2" data-id="element-30">
                  Tu lugar estará reservado durante <strong data-id="element-31">15 minutos</strong>.
                  En caso de no acceder, se cederá el descuento a la siguiente
                  persona.
                </p>
                <div className="flex items-end justify-center gap-3" data-id="element-32">
                  <div className="flex flex-col items-center" data-id="element-33">
                    <span className="text-3xl font-bold text-[#C17B5C] tabular-nums leading-none" data-id="element-34">
                      {minutes}
                    </span>
                    <span className="text-xs text-[#8B7355] uppercase tracking-wider mt-1" data-id="element-35">
                      Minutos
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-[#C17B5C] mb-2" data-id="element-36">
                    :
                  </span>
                  <div className="flex flex-col items-center" data-id="element-37">
                    <span className="text-3xl font-bold text-[#C17B5C] tabular-nums leading-none" data-id="element-38">
                      {seconds}
                    </span>
                    <span className="text-xs text-[#8B7355] uppercase tracking-wider mt-1" data-id="element-39">
                      Segundos
                    </span>
                  </div>
                </div>
              </div>

              <Button size="md" fullWidth href="https://factorclaro.com//cart/45275718320307:1" target="_blank" rel="noopener noreferrer" className="text-base h-12 shadow-xl shadow-[#C17B5C]/20" data-id="element-40">
                👉 ACCESO CON 50% OFF 👈
              </Button>

              <div className="flex flex-col items-center gap-2 text-sm text-[#6B5A4E]" data-id="element-41">
                <button onClick={() => setGuaranteeOpen(v => !v)} className="flex items-center gap-2 cursor-pointer hover:text-[#3D2B1F] transition-colors group" data-id="element-42">
                  <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-43" />
                  <span data-id="element-44">Pago 100% seguro • Entrega inmediata por email</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-green-600 transition-transform duration-300 ${guaranteeOpen ? 'rotate-180' : ''}`} data-id="element-45" />
                </button>

                <AnimatePresence data-id="element-46">
                  {guaranteeOpen && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} className="overflow-hidden w-full" data-id="element-47">
                      <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mt-1 text-left" data-id="element-48">
                        <ShieldCheck className="w-8 h-8 text-green-600 flex-shrink-0 mt-0.5" data-id="element-49" />
                        <div data-id="element-50">
                          <p className="font-bold text-green-800 text-sm" data-id="element-51">
                            Garantía de 7 días sin preguntas
                          </p>
                          <p className="text-green-700 text-xs mt-0.5 leading-relaxed" data-id="element-52">
                            Si por cualquier motivo no estás satisfecha con la
                            Biblioteca, te devolvemos el 100% de tu dinero
                            dentro de los 7 días posteriores a la compra. Sin
                            preguntas, sin complicaciones.
                          </p>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Garantía 7 días - siempre visible */}
              <div className="w-full flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-left" data-id="element-53">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-54">
                  <ShieldCheck className="w-5 h-5 text-green-600" data-id="element-55" />
                </div>
                <div data-id="element-56">
                  <p className="font-bold text-green-800 text-sm" data-id="element-57">
                    🛡️ Garantía de 7 días sin preguntas
                  </p>
                  <p className="text-green-700 text-xs mt-0.5 leading-relaxed" data-id="element-58">
                    Si no estás satisfecha, te devolvemos el 100% de tu dinero.
                    Sin preguntas, sin complicaciones.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}